import React, { useState, useEffect } from "react";
import {
  addMonths,
  format
} from "date-fns";
import {
  InputNumber,
  DatePicker,
  Button,
  Card,
  Table,
  Typography,
  Row,
  Col,
  Divider
} from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";

const { Title, Text } = Typography;

const UpfrontProfitCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [downPayment, setDownPayment] = useState(3366.67);
  const [residualValue, setResidualValue] = useState(2000);
  const [interestRate, setInterestRate] = useState(4.5);
  const [loanTenure, setLoanTenure] = useState(12);
  const [startDate, setStartDate] = useState("2009-12-23");

  const financedAmount = loanAmount - downPayment;

  const calculateEMI = () => {
    const principal = financedAmount - residualValue;
    const monthlyRate = interestRate / 100 / 12;
    const totalPayments = loanTenure;

    if (monthlyRate === 0) {
      return principal / totalPayments;
    }

    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
      (Math.pow(1 + monthlyRate, totalPayments) - 1)
    );
  };

  const generateSchedule = () => {
    let schedule = [];
    let balance = financedAmount;
    let currentDate = new Date(startDate);
    let emi = calculateEMI();
    let totalInterest = 0;

    for (let i = 0; i < loanTenure; i++) {
      let interestPayment = balance * (interestRate / 100 / 12);
      let principalPayment = emi - interestPayment;

      if (i === loanTenure - 1) {
        principalPayment = balance - residualValue;
        emi = principalPayment + interestPayment;
        balance = residualValue;
      } else {
        balance -= principalPayment;
      }

      totalInterest += interestPayment;

      let formattedDate = format(currentDate, "dd-MMM-yy");

      schedule.push({
        key: i,
        date: formattedDate,
        emi,
        interestPayment,
        principalPayment,
        balance,
      });

      currentDate = addMonths(currentDate, 1);
    }

    return { schedule, upfrontProfit: totalInterest };
  };

  const [schedule, setSchedule] = useState([]);
  const [upfrontProfit, setUpfrontProfit] = useState(0);

  const handleCalculate = () => {
    const { schedule, upfrontProfit } = generateSchedule();
    setSchedule(schedule);
    setUpfrontProfit(upfrontProfit);
  };

  useEffect(() => {
    handleCalculate();
  }, [loanAmount, interestRate, loanTenure, residualValue, downPayment]);

  const downloadCSV = () => {
    const headers = ["Date", "EMI", "Interest", "Principal", "Outstanding"];
    const rows = schedule.map((row) =>
      [row.date, row.emi, row.interestPayment, row.principalPayment, row.balance]
        .map((val) => `"${val.toFixed ? val.toFixed(2) : val}"`)
        .join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "amortization_schedule.csv";
    link.click();
  };

  const columns = [
    { title: "Date", dataIndex: "date", align: "center" },
    {
      title: "EMI",
      dataIndex: "emi",
      render: (val) => `$${val.toFixed(2)}`,
      align: "center"
    },
    {
      title: "Interest",
      dataIndex: "interestPayment",
      render: (val) => `$${val.toFixed(2)}`,
      align: "center"
    },
    {
      title: "Principal",
      dataIndex: "principalPayment",
      render: (val) => `$${val.toFixed(2)}`,
      align: "center"
    },
    {
      title: "Outstanding",
      dataIndex: "balance",
      render: (val) => `$${val.toFixed(2)}`,
      align: "center"
    },
  ];

  const tableHeaderStyle = {
    backgroundColor: "#e8efff",
    color: "#0d1730",
    fontWeight: "bold",
    textAlign: "center",
  };

  return (
    <div
      style={{
        padding: 40,
        maxWidth: 1200,
        margin: "auto",
        backgroundColor: "#f5f7fa",
        borderRadius: 12,
      }}
    >
      <Title level={2} style={{ textAlign: "center", color: "#1f3b73" }}>
        Upfront Profit Calculator
      </Title>

      <Card
        style={{
          marginTop: 24,
          backgroundColor: "#ffffff",
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Text strong>Loan Amount</Text>
            <InputNumber
              value={loanAmount}
              onChange={setLoanAmount}
              min={0}
              precision={2}
              style={{ width: "100%", marginBottom: 20 }}
            />

            <Text strong>Down Payment</Text>
            <InputNumber
              value={downPayment}
              onChange={setDownPayment}
              min={0}
              precision={2}
              style={{ width: "100%", marginBottom: 20 }}
            />

            <Text strong>Residual Value</Text>
            <InputNumber
              value={residualValue}
              onChange={setResidualValue}
              min={0}
              precision={2}
              style={{ width: "100%", marginBottom: 20 }}
            />
          </Col>

          <Col span={12}>
            <Text strong>Interest Rate (%)</Text>
            <InputNumber
              value={interestRate}
              onChange={setInterestRate}
              min={0}
              precision={2}
              style={{ width: "100%", marginBottom: 20 }}
            />

            <Text strong>Loan Tenure (Months)</Text>
            <InputNumber
              value={loanTenure}
              onChange={setLoanTenure}
              min={1}
              precision={0}
              style={{ width: "100%", marginBottom: 20 }}
            />

            <Text strong>Start Date</Text>
            <DatePicker
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) =>
                setStartDate(date ? date.format("YYYY-MM-DD") : "")
              }
              style={{ width: "100%", marginBottom: 20 }}
            />
          </Col>
        </Row>

        <Divider />

        <Row justify="space-between" align="middle">
          <Col>
            <Text strong>EMI: </Text>
            <Text type="success">${calculateEMI().toFixed(2)}</Text>
          </Col>
          <Col>
            <Text strong>Upfront Profit: </Text>
            <Text type="danger">${upfrontProfit.toFixed(2)}</Text>
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#0d1730",
              padding: "12px 0",
              textAlign: "center",
            }}
          >
            Amortization Schedule
          </div>
        }
        style={{
          marginTop: 32,
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        }}
        extra={
          <Button type="primary" onClick={downloadCSV}>
            Download CSV
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={schedule}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: true }}
          components={{
            header: {
              cell: (props) => (
                <th style={{ ...tableHeaderStyle, ...props.style }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </Card>
    </div>
  );
};

export default UpfrontProfitCalculator;
